import React, { useEffect, useState, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface RateCap {
  role?: string;
  max_rate: number;
}

interface GuidelineRules {
  rate_caps?: RateCap[];
  max_hours_per_entry?: number;
  min_description_length?: number;
  block_billing_threshold_hours?: number;
  prohibited_utbms_codes?: string[];
  prohibited_keywords?: string[];
  require_utbms_codes?: boolean;
  require_task_per_entry?: boolean;
  max_total_per_matter?: number;
  enforce_fee_splits?: boolean;
  max_co_counsel_rate?: number;
}

interface CoCounselOption {
  id: string;
  name: string;
  firm: string;
  status: string;
}

interface BillingGuideline {
  guideline_id: string;
  realm_id: string;
  client_id: string | null;
  co_counsel_id: string | null;
  name: string;
  is_active: boolean;
  rules: GuidelineRules | null;
  effective_date: string | null;
  end_date: string | null;
  created_at: string;
  client?: { client_id: string; client_name: string } | null;
  co_counsel?: { co_counsel_id: string; name: string; firm: string } | null;
}

type FormMode = 'closed' | 'create' | 'edit';

const EMPTY_RULES: GuidelineRules = {
  rate_caps: [],
  prohibited_utbms_codes: [],
  prohibited_keywords: [],
};

const GuidelinesPage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();
  const [guidelines, setGuidelines] = useState<BillingGuideline[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formMode, setFormMode] = useState<FormMode>('closed');
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formClientId, setFormClientId] = useState('');
  const [formCoCounselId, setFormCoCounselId] = useState('');
  const [formEffectiveDate, setFormEffectiveDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formRules, setFormRules] = useState<GuidelineRules>({ ...EMPTY_RULES });
  const [formActive, setFormActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [coCounselOptions, setCoCounselOptions] = useState<CoCounselOption[]>([]);

  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchGuidelines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/billing/guidelines', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load guidelines.');
      const body = await res.json();
      setGuidelines(body.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchGuidelines();
  }, [fetchGuidelines]);

  // Fetch co-counsel options when client changes
  useEffect(() => {
    if (!formClientId || formMode === 'closed') {
      setCoCounselOptions([]);
      if (!formClientId) setFormCoCounselId('');
      return;
    }
    const fetchCoCounsel = async () => {
      try {
        const res = await fetch(`/api/v1/clients/${formClientId}/co-counsel`, { headers: getAuthHeaders() });
        if (!res.ok) { setCoCounselOptions([]); return; }
        const body = await res.json();
        setCoCounselOptions((body.data || []).filter((cc: CoCounselOption) => cc.status === 'active'));
      } catch {
        setCoCounselOptions([]);
      }
    };
    fetchCoCounsel();
  }, [formClientId, formMode, getAuthHeaders]);

  const openCreate = () => {
    setFormMode('create');
    setEditId(null);
    setFormName('');
    setFormClientId('');
    setFormCoCounselId('');
    setFormEffectiveDate('');
    setFormEndDate('');
    setFormRules({ ...EMPTY_RULES });
    setFormActive(true);
  };

  const openEdit = (g: BillingGuideline) => {
    setFormMode('edit');
    setEditId(g.guideline_id);
    setFormName(g.name);
    setFormClientId(g.client_id || '');
    setFormCoCounselId(g.co_counsel_id || '');
    setFormEffectiveDate(g.effective_date?.split('T')[0] || '');
    setFormEndDate(g.end_date?.split('T')[0] || '');
    setFormRules(g.rules || { ...EMPTY_RULES });
    setFormActive(g.is_active);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        name: formName,
        rules: formRules,
        effective_date: formEffectiveDate || undefined,
        end_date: formEndDate || undefined,
      };
      if (formMode === 'create') {
        payload.client_id = formClientId || undefined;
        payload.co_counsel_id = formCoCounselId || undefined;
      }
      if (formMode === 'edit') {
        payload.is_active = formActive;
      }

      const url = formMode === 'create'
        ? '/api/v1/billing/guidelines'
        : `/api/v1/billing/guidelines/${editId}`;
      const method = formMode === 'create' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || 'Save failed.');
      }
      setFormMode('closed');
      fetchGuidelines();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this guideline?')) return;
    try {
      const res = await fetch(`/api/v1/billing/guidelines/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed.');
      fetchGuidelines();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Rule helpers
  const updateRule = <K extends keyof GuidelineRules>(key: K, value: GuidelineRules[K]) => {
    setFormRules((prev) => ({ ...prev, [key]: value }));
  };

  const addRateCap = () => {
    updateRule('rate_caps', [...(formRules.rate_caps || []), { role: '', max_rate: 0 }]);
  };

  const removeRateCap = (idx: number) => {
    updateRule('rate_caps', (formRules.rate_caps || []).filter((_, i) => i !== idx));
  };

  const updateRateCap = (idx: number, field: keyof RateCap, value: any) => {
    const caps = [...(formRules.rate_caps || [])];
    caps[idx] = { ...caps[idx], [field]: value };
    updateRule('rate_caps', caps);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/billing')}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Billing
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing Guidelines</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage outside counsel guidelines and billing compliance rules per client.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Guideline
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Form panel */}
      {formMode !== 'closed' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {formMode === 'create' ? 'New Guideline' : 'Edit Guideline'}
            </h2>
            <button onClick={() => setFormMode('closed')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Basic fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Acme Corp Outside Counsel Guidelines"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            {formMode === 'create' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client ID <span className="text-gray-400">(optional, blank = firm-wide)</span>
                </label>
                <input
                  type="text"
                  value={formClientId}
                  onChange={(e) => setFormClientId(e.target.value)}
                  placeholder="UUID or leave blank for firm-wide"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}
            {formMode === 'create' && formClientId && coCounselOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Co-Counsel <span className="text-gray-400">(optional, blank = client-wide)</span>
                </label>
                <select
                  value={formCoCounselId}
                  onChange={(e) => setFormCoCounselId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">All (Client-wide)</option>
                  {coCounselOptions.map((cc) => (
                    <option key={cc.id} value={cc.id}>{cc.name} — {cc.firm}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective Date</label>
              <input
                type="date"
                value={formEffectiveDate}
                onChange={(e) => setFormEffectiveDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={formEndDate}
                onChange={(e) => setFormEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Active toggle (edit mode) */}
          {formMode === 'edit' && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} className="rounded border-gray-300 dark:border-gray-600" />
              Active
            </label>
          )}

          {/* Rules */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Rules</h3>

            {/* Rate caps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Rate Caps</label>
                <button onClick={addRateCap} className="text-xs text-lex-accent hover:underline">+ Add cap</button>
              </div>
              {(formRules.rate_caps || []).map((cap, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={cap.role || ''}
                    onChange={(e) => updateRateCap(idx, 'role', e.target.value)}
                    placeholder="Role (e.g. Partner)"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                  <input
                    type="number"
                    value={cap.max_rate || ''}
                    onChange={(e) => updateRateCap(idx, 'max_rate', parseFloat(e.target.value) || 0)}
                    placeholder="Max $/hr"
                    className="w-28 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                  <button onClick={() => removeRateCap(idx)} className="text-red-400 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Numeric rules */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Hours / Entry</label>
                <input
                  type="number"
                  step="0.5"
                  value={formRules.max_hours_per_entry ?? ''}
                  onChange={(e) => updateRule('max_hours_per_entry', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="e.g. 8"
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min Description Length</label>
                <input
                  type="number"
                  value={formRules.min_description_length ?? ''}
                  onChange={(e) => updateRule('min_description_length', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g. 20"
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Block Billing Threshold (hrs)</label>
                <input
                  type="number"
                  step="0.5"
                  value={formRules.block_billing_threshold_hours ?? ''}
                  onChange={(e) => updateRule('block_billing_threshold_hours', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="e.g. 4"
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>

            {/* Max total per matter */}
            <div className="w-1/3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Total / Matter ($)</label>
              <input
                type="number"
                value={formRules.max_total_per_matter ?? ''}
                onChange={(e) => updateRule('max_total_per_matter', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="e.g. 50000"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>

            {/* Prohibited UTBMS codes */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prohibited UTBMS Codes <span className="text-gray-400">(comma separated)</span>
              </label>
              <input
                type="text"
                value={(formRules.prohibited_utbms_codes || []).join(', ')}
                onChange={(e) => updateRule('prohibited_utbms_codes', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                placeholder="e.g. L210, E110"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>

            {/* Prohibited keywords */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prohibited Keywords <span className="text-gray-400">(comma separated)</span>
              </label>
              <input
                type="text"
                value={(formRules.prohibited_keywords || []).join(', ')}
                onChange={(e) => updateRule('prohibited_keywords', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                placeholder="e.g. travel time, office conference"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>

            {/* Boolean toggles */}
            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formRules.require_utbms_codes ?? false}
                  onChange={(e) => updateRule('require_utbms_codes', e.target.checked || undefined)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Require UTBMS Codes
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formRules.require_task_per_entry ?? false}
                  onChange={(e) => updateRule('require_task_per_entry', e.target.checked || undefined)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Require Task per Entry
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formRules.enforce_fee_splits ?? false}
                  onChange={(e) => updateRule('enforce_fee_splits', e.target.checked || undefined)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Enforce Fee Splits
              </label>
            </div>

            {/* Co-counsel rate cap */}
            <div className="w-1/3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Co-Counsel Rate ($/hr)</label>
              <input
                type="number"
                value={formRules.max_co_counsel_rate ?? ''}
                onChange={(e) => updateRule('max_co_counsel_rate', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="e.g. 450"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setFormMode('closed')}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !formName}
              className="px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Saving...' : formMode === 'create' ? 'Create Guideline' : 'Update Guideline'}
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {!initialLoadDone && (
        <div className="flex items-center justify-center py-10">
          <div className="h-6 w-6 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading...</span>
        </div>
      )}

      {/* Guidelines list */}
      {initialLoadDone && guidelines.length === 0 && (
        <div className="text-center py-10">
          <ShieldExclamationIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No billing guidelines configured yet.
          </p>
        </div>
      )}

      {initialLoadDone && guidelines.length > 0 && (
        <div className="space-y-3">
          {guidelines.map((g) => (
            <div
              key={g.guideline_id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{g.name}</h3>
                    {g.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        <CheckCircleIcon className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {g.client?.client_name || 'Firm-wide'}
                    {g.co_counsel && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-lex-accent-soft text-lex-accent">
                        Co-Counsel: {g.co_counsel.name}
                      </span>
                    )}
                    {g.effective_date && ` | Effective: ${new Date(g.effective_date).toLocaleDateString()}`}
                    {g.end_date && ` — ${new Date(g.end_date).toLocaleDateString()}`}
                  </p>
                  {/* Rule summary */}
                  {g.rules && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(g.rules.rate_caps?.length ?? 0) > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-lex-accent-soft text-lex-accent">
                          {g.rules.rate_caps!.length} rate cap{g.rules.rate_caps!.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {g.rules.max_hours_per_entry && (
                        <span className="text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">
                          Max {g.rules.max_hours_per_entry}h/entry
                        </span>
                      )}
                      {g.rules.min_description_length && (
                        <span className="text-xs px-2 py-0.5 rounded bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300">
                          Min {g.rules.min_description_length} chars
                        </span>
                      )}
                      {g.rules.require_utbms_codes && (
                        <span className="text-xs px-2 py-0.5 rounded bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300">
                          UTBMS required
                        </span>
                      )}
                      {(g.rules.prohibited_keywords?.length ?? 0) > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                          {g.rules.prohibited_keywords!.length} banned keyword{g.rules.prohibited_keywords!.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {g.rules.enforce_fee_splits && (
                        <span className="text-xs px-2 py-0.5 rounded bg-lex-accent-soft text-lex-accent">
                          Fee splits enforced
                        </span>
                      )}
                      {g.rules.max_co_counsel_rate && (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                          Co-counsel cap ${g.rules.max_co_counsel_rate}/hr
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(g)}
                    className="p-1.5 text-gray-400 hover:text-lex-accent rounded"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(g.guideline_id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuidelinesPage;
